import {forwardRef, useContext, useImperativeHandle, useState} from 'react'
import {useParams} from 'react-router-dom'
import {Button, Modal, Result} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {getRequest, postRequest} from '../../services/base'
import {NavContext} from '../../context/global_context'
import {useIntl} from 'react-intl'

const DtcValidationRunner = forwardRef(
  ({ checkValidationFetchingProgress }, ref) => {
    const intl = useIntl()
    const { vehicle } = useParams()
    const [versionsData, setVersionsData] = useState({})
    const [showModal, setShowModal] = useState(false)
    const [dataError, setDataError] = useState(null)
    const [, navDispatch] = useContext(NavContext)

    const getTracerVersions = async () => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'dtcValidation.loadingVersions' }),
          showLoader: true,
        },
        notification: {},
      })
      let res = await getRequest(
        `/api/dtc_validation/tracer-data-version/${vehicle}/`
      )
      if (!res?.data?.data_version || !res?.data?.app_version) {
        setDataError(true)
      } else {
        setDataError(false)
        setVersionsData(res.data || {})
      }

      navDispatch({
        type: 'loader',
        data: {
          showLoader: false,
        },
        notification: {},
      })
    }

    useImperativeHandle(ref, () => ({
      openModal: () => {
        getTracerVersions()
        setShowModal(true)
      },
    }))

    const startValidationProcess = async () => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'dtcValidation.startingProcess' }),
          showLoader: true,
        },
        notification: {},
      })

      let payload = {
        app_version: versionsData.app_version,
        data_version: versionsData.data_version,
      }
      let res = await postRequest(
        `/api/dtc_validation/validation/run/${vehicle}/`,
        payload
      )
      navDispatch({
        type: 'loader',
        data: {
          showLoader: false,
        },
        notification: {},
      })
      if (res?.status) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'dtcValidation.runSuccess' }),
          },
        })
        setShowModal(false)
        checkValidationFetchingProgress?.()
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'dtcValidation.runError' }),
          },
        })
      }
    }

    const confirmStartProcess = () => {
      Modal.confirm({
        title: intl.formatMessage({
          id: 'dtcValidation.confirmStartProcessTitle',
        }),
        icon: <ExclamationCircleOutlined />,
        content: intl.formatMessage({
          id: 'dtcValidation.confirmStartProcessContent',
        }),
        onOk: startValidationProcess,
        centered: true,
        maskClosable: true,
      })
    }

    return (
      <Modal
        title={intl.formatMessage({ id: 'dtcValidation.tracerVersionTitle' })}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
      >
        {dataError ? (
          <Result
            icon={<ExclamationCircleOutlined style={{ color: 'var(--color-warning-icon)' }} />}
            title={intl.formatMessage({ id: 'dtcValidation.versionNotAvailable' })}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ padding: '8px 0' }}>
              {intl.formatMessage({ id: 'dtcValidation.appVersion' })}:{' '}
              {versionsData.app_version || '-'}
            </div>
            <div style={{ padding: '8px 0' }}>
              {intl.formatMessage({ id: 'dtcValidation.dataVersion' })}:{' '}
              {versionsData.data_version || '-'}
            </div>
            <div style={{ padding: '24px 0 0' }}>
              <Button type="primary" onClick={confirmStartProcess}>
                {intl.formatMessage({ id: 'dtcValidation.startProcess' })}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    )
  }
)

export default DtcValidationRunner
